import React from "react";
import { Modal, Button } from "react-bootstrap";
import styles from "./styles.module.css";
import LightningBoltIcon from "components/common/icons/LightningBoltIcon";
import { useAppSelector } from "state/hooks.ts";
import { DOLLAR_SIGN, getGroupCurrencySymbol, getGroupStyles } from "state/slices/group-slice.ts";
import { textColor } from "utils/constants.ts";
import { ContestType, isBasicContest } from "interfaces/leaderboard/contest";

interface Props {
  show: boolean;
  entryFee: number;
  onHide: () => void;
  initialBalance: number;
  contestType?: ContestType;
  isPickPerPool?: boolean;
}

const PRO_MIN_UNIQUE_PICKS = 3;

const ContestPostJoinInfoModal: React.FC<Props> = ({ show, entryFee, onHide, initialBalance, contestType, isPickPerPool }) => {
  const currencySymbol = useAppSelector(state => getGroupCurrencySymbol(state));
  const primaryColor = useAppSelector(getGroupStyles).primary_color;

  const isBasic = isBasicContest({ type: contestType });
  const contestInitialBalance = (
    <span className={styles.boldUnderline}>
      {isBasic ? `${initialBalance} Picks` : `${currencySymbol}${initialBalance} Sparks`}
    </span>
  );
  const requiredUniquePicks = isBasic ? initialBalance : PRO_MIN_UNIQUE_PICKS;
  const inlineSpark = <span className={styles.bold}>§</span>;
  return (
    <Modal show={show} onHide={onHide} centered dialogClassName={styles.modalDialog}>
      <Modal.Body className={styles.modalBody} style={{ backgroundColor: primaryColor }}>
        <button onClick={onHide} style={{ color: textColor }} className={styles.closeButton} aria-label="Close">
          &times;
        </button>
        <LightningBoltIcon className={styles.lightningIcon} color={textColor} />

        <div className={styles.mainContentFrame}>
          <div className={styles.headerText}>
            Your <span className={styles.boldUnderline}>{entryFee === 0 ? "FREE" :
            <span>{DOLLAR_SIGN}{entryFee}</span>} </span>
            entry gives you&nbsp;
            {contestInitialBalance}<span className={styles.boldUnderline}>.</span>
          </div>

          <div className={styles.stepsContainer}>
            <div className={styles.stepItem}>
              <div className={styles.stepNumber} style={{ color: primaryColor, backgroundColor: textColor }}>1</div>
              <div className={styles.stepText}>
                {isPickPerPool ? (
                  <>
                    Make <span className={styles.bold}>one pick on each of the {requiredUniquePicks} pools</span> for a valid entry.
                  </>
                ) : isBasic ? (
                  <>
                    Make picks on <span className={styles.bold}>{requiredUniquePicks} different contestants</span> for a valid entry.
                  </>
                ) : (
                  <>
                    Use your {contestInitialBalance} to make <span className={styles.bold}>at least {requiredUniquePicks} unique picks.</span>
                  </>
                )}
              </div>
            </div>
            <div className={styles.stepItem}>
              <div className={styles.stepNumber} style={{ color: primaryColor, backgroundColor: textColor }}>2</div>
              <div className={styles.stepText}>
                {isBasic ? (
                  <>
                    Each correct pick wins you <span className={styles.bold}>Sparks</span> {inlineSpark} based on the odds of your pick.
                  </>
                ) : (
                  <>
                    Climb the <span className={styles.bold}>Leaderboard</span> by winning picks.
                  </>
                )}
              </div>
            </div>
            <div className={styles.stepItem}>
              <div className={styles.stepNumber} style={{ color: primaryColor, backgroundColor: textColor }}>3</div>
              <div className={styles.stepText}>
                {isBasic ? (
                  <>
                    Earn {inlineSpark} to <span className={styles.bold}>climb the Leaderboard</span> — prizes go to top finishers.
                  </>
                ) : (
                  <>
                    <span className={styles.bold}>Prizes</span> are awarded based on
                    <span className={styles.bold}> Leaderboard</span> position.
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.proTip}>
          <span className={styles.bold}>Pro Tip: </span>
          {isBasic ? (
            <>
              Odds are dynamic until the pool closes. Some contests offer
              <span className={styles.bold}> 'Exacta'</span> (top two finishers in order) or
              <span className={styles.bold}> 'Trifecta'</span> (top three). These count as
              <span className={styles.bold}> 2 or 3 picks</span> respectively.
            </>
          ) : (
            <>
              Use your <span className={styles.bold}>skill </span>
              to <span className={styles.bold}>pick the best odds</span> to
              <span className={styles.bold}> earn the most Sparks. Odds are dynamic </span>
              based on user activity and can change until the close time of each pool.
            </>
          )}
        </div>

        <div className={styles.closeButtonContainer}>
          <Button 
            onClick={onHide}
            className={styles.bottomCloseButton}
            style={{ 
              backgroundColor: textColor,
              color: primaryColor,
              border: 'none',
              padding: '8px 24px',
              borderRadius: '4px',
              fontWeight: 'bold'
            }}
          >
            Let's Go!
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ContestPostJoinInfoModal;